/**
 * Copyright 2020-2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { Component } from 'react';
import StatusTimeline from '../../timeline/StatusTimeline';
import DetailGallery from '../common/DetailGallery';
import { Accordion, Row, Col, Badge } from 'react-bootstrap';
import { DetailAccordionCard, DetailHallAndBooth, DetailName, labels } from '@umati/dashboard-common';
import SectionData from '../../machine/sectionData';
import ScaleUtil from './ScaleUtil';
import ScaleRange from './ScaleRange';
import Override from '../../machine/override';
import classNames from 'classnames';
import ScaleSimulation from './ScaleSimulation';
import InRange from './InRange';
import Header from '../../header/header';
import { connect } from 'react-redux';
import { getFairs, getMachines } from '../../../redux/selectors';

class ScaleDetail extends Component {
  constructor(props) {
    super(props);
    this.interval = -1;
    this.simulate = this.simulate.bind(this);
    this.preset = this.preset.bind(this);
    this.simulation = new ScaleSimulation(this.state);
  }

  // execute several last steps to prefill the table of recent registeed weights
  preset() {
    const time = Math.floor(Date.now() / 1000);
    for (let i = 200; i >= 0; --i) {
      this.simulation.simulate(time - i);
    }
    this.setState(this.simulation.state);
  }

  componentDidMount() {
    this.interval = setInterval(this.simulate, 300);
    this.preset();
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  state = {
    sim: {
      tare: {
        num: 0.025,
        unit: 'kg',
      },
      offset: 0.062,
      lastStateNum: -1,
    },
    data: {
      identification: {
        Name: 'Example Scale',
        DeviceClass: 'SimpleScale',
        Model: 'Printing Scale',
        Manufacturer: 'Umati Team',
        SerialNumber: '123-46',
        SoftwareRevision: '1.2.1',
        HardwareRevision: '2',
        isOnline: true,
      },
      hall: 1,
      booth: 'B2',
      labelLength: '80mm',
      labelHeight: '30mm',
      labelStock: 90,
      printMediaStock: 23,
      inRange: InRange.Normal,
      recentWeights: [
        {
          net: {
            num: 2.456,
            unit: 'kg',
          },
          gross: {
            num: 2.481,
            unit: 'kg',
          },
          tare: {
            num: 0.025,
            unit: 'kg',
          },
        },
      ],
      regWeight: {
        net: {
          num: 2.456,
          unit: 'kg',
        },
        gross: {
          num: 2.481,
          unit: 'kg',
        },
        tare: {
          num: 0.025,
          unit: 'kg',
        },
      },
      curWeight: {
        net: {
          num: 2.456,
          unit: 'kg',
        },
        gross: {
          num: 2.481,
          unit: 'kg',
        },
        tare: {
          num: 0.025,
          unit: 'kg',
        },
      },
      activeRange: 0,
      weighingRanges: [
        {
          min: {
            num: 0.1,
            unit: 'kg',
          },
          max: {
            num: 10,
            unit: 'kg',
          },
          e: {
            num: 1,
            unit: 'g',
          },
        },
        {
          min: {
            num: 10,
            unit: 'kg',
          },
          max: {
            num: 100,
            unit: 'kg',
          },
          e: {
            num: 10,
            unit: 'g',
          },
        },
      ],
    },
  };

  simulate() {
    const time = Math.floor(Date.now() / 1000);
    if (this.simulation.simulate(time)) {
      this.setState(this.simulation.state);
    }
  }

  render() {
    const identification = this.state.data.identification;
    return (
      <div>
        {' '}
        {identification && <Header {...this.props} fairs={this.props.fairs} machineIdentification={identification} />}
        <div className="view-machine-detail scale-detail">
          <div className="hero">
            <div className="container-max">
              <DetailName
                isOnline={this.state.data.identification.isOnline}
                name={this.state.data.identification.Model}
                model={this.state.data.identification.Name}
              />
              <DetailHallAndBooth hall={this.state.data.hall} booth={this.state.data.booth} />
            </div>
          </div>
          <div className="container-max">
            {this.ImageAndTimeline(null)}
            <div className="row justify-content-end">
              <div className="col-12 offset-lg-2 col-lg-9">
                <Accordion defaultActiveKey="0" className="mb-4">
                  {this.OverviewCard()}
                  {this.InformationCard()}
                  {this.WeighingRangeCard()}
                  {this.LabelPrinterCard()}
                </Accordion>
              </div>
              <div className="col-12 col-lg-1" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  ImageAndTimeline(timeData) {
    return (
      <div className="row mb-4">
        <div className="col-12 col-lg-5">
          <DetailGallery img={process.env.PUBLIC_URL + '/test-image.png'} />
        </div>
        <div className="col-12 col-lg-7">
          <div className="mb-1 mb-md-2 mb-lg-4">
            <h3 className="h5">{labels.statusOverview}</h3>
            <div className="status-timeline">{timeData && <StatusTimeline timeData={timeData} />}</div>
          </div>
        </div>
      </div>
    );
  }

  InRangeToBadge(inRange) {
    switch (inRange) {
      case InRange.Normal: {
        return <Badge variant="success">In Range</Badge>;
      }
      case InRange.Overload: {
        return <Badge variant="danger">Overload</Badge>;
      }
      case InRange.Underload: {
        return <Badge variant="danger">Underload</Badge>;
      }
      default:
        return <Badge>Unknown</Badge>;
    }
  }

  OverviewCard() {
    const WeighingRange = this.WeighingRange;
    const netClassNames = classNames('head-weight font-weight-bold', {
      highlighted: this.state.data.highlightNew,
    });
    return (
      <DetailAccordionCard eventKey="0" title="Overview" isExpanded={true}>
        <div className="section overview">
          <Row className="section">
            <Col xs="12" className="head-weight title">
              Registered Weight
            </Col>
            <Col xs="4" className={netClassNames}>
              Net: {ScaleUtil.WeightValue(this.state.data.regWeight.net)}
            </Col>
            <Col xs="4" className="head-weight normal">
              Gross: {ScaleUtil.WeightValue(this.state.data.regWeight.gross)}
            </Col>
            <Col xs="4" className="head-weight normal">
              Tare (Preset): {ScaleUtil.WeightValue(this.state.data.regWeight.tare)}
            </Col>
          </Row>
          <Row className="section">
            <Col xs={{ span: 12 }} className="overload">
              {this.InRangeToBadge(this.state.data.inRange)}
            </Col>
            <Col md={{ span: 12 }}>
              <Row>
                <Col className="desc">Current Weight (Weighing Range ID = {this.state.data.activeRange})</Col>
              </Row>
              <WeighingRange {...this.state.data.weighingRanges[this.state.data.activeRange]} processBarLabel={true} />
            </Col>
            <Col md={{ span: 12 }} className="range">
              <ScaleRange
                {...this.state.data.weighingRanges[this.state.data.activeRange]}
                value={this.state.data.curWeight.gross}
              />
            </Col>
            <Col xs={{ span: 4 }}>
              <SectionData label="Net" value={ScaleUtil.WeightValue(this.state.data.curWeight.net)} />
            </Col>
            <Col xs={{ span: 4 }}>
              <SectionData label="Gross" value={ScaleUtil.WeightValue(this.state.data.curWeight.gross)} />
            </Col>
            <Col xs={{ span: 4 }}>
              <SectionData label="Tare (Preset)" value={ScaleUtil.WeightValue(this.state.data.curWeight.tare)} />
            </Col>
          </Row>
          <Row className="section">
            <ScaleDetail.LastRegWeights weights={this.state.data.recentWeights} />
          </Row>
        </div>
      </DetailAccordionCard>
    );
  }

  static LastRegWeights(props) {
    const WeightRow = function (regWeight) {
      return (
        <>
          <Col xs="4">{ScaleUtil.WeightValue(regWeight.net)}</Col>
          <Col xs="4">{ScaleUtil.WeightValue(regWeight.gross)}</Col>
          <Col xs="4">{ScaleUtil.WeightValue(regWeight.tare)}</Col>
        </>
      );
    };

    return (
      <>
        <Col xs="12" className="desc">
          Recent Registered Weights
        </Col>
        <Col xs="4" className="label">
          Net
        </Col>
        <Col xs="4" className="label">
          Gross
        </Col>
        <Col xs="4" className="label">
          Tare (Preset)
        </Col>
        {props.weights && props.weights.map((wr, index) => <WeightRow key={index} {...wr} />)}
      </>
    );
  }

  WeighingRangeCard() {
    const WeighingRange = this.WeighingRange;
    const wrs = this.state.data.weighingRanges;
    return (
      <DetailAccordionCard eventKey="2" title="Weighing Ranges">
        <div className="section">{wrs && wrs.map((wr, index) => <WeighingRange key={index} {...wr} />)}</div>
      </DetailAccordionCard>
    );
  }

  WeighingRange(props) {
    const maxClNames = classNames('col-4', {
      'text-right': props.processBarLabel,
    });
    const eClNames = classNames('col-4', {
      'text-center': props.processBarLabel,
    });
    return (
      <Row className="section">
        <SectionData cols="col-4" label="Min" value={ScaleUtil.WeightValue(props.min)} />
        <SectionData cols={eClNames} label="e" value={ScaleUtil.WeightValue(props.e)} />
        <SectionData cols={maxClNames} label="Max" value={ScaleUtil.WeightValue(props.max)} />
      </Row>
    );
  }

  InformationCard() {
    function Prop(props) {
      return <SectionData cols="col-12 col-sm-6 col-md-4" {...props} />;
    }
    return (
      <DetailAccordionCard eventKey="3" title="Information">
        <Row className="section">
          {this.state.data.identification.DeviceClass && (
            <Prop label="DeviceClass" value={this.state.data.identification.DeviceClass} />
          )}
          {this.state.data.identification.Model && <Prop label="Model" value={this.state.data.identification.Model} />}
          {this.state.data.identification.Manufacturer && (
            <Prop label="Manufacturer" value={this.state.data.identification.Manufacturer} />
          )}
          {this.state.data.identification.SerialNumber && (
            <Prop label="SerialNumber" value={this.state.data.identification.SerialNumber} />
          )}
          {this.state.data.identification.SoftwareRevision && (
            <Prop label="SoftwareRevision" value={this.state.data.identification.SoftwareRevision} />
          )}
          {this.state.data.identification.HardwareRevision && (
            <Prop label="HardwareRevision" value={this.state.data.identification.HardwareRevision} />
          )}
        </Row>
      </DetailAccordionCard>
    );
  }

  LabelPrinterCard() {
    return (
      <DetailAccordionCard eventKey="1" title="Label Printer">
        <Row className="section label-printer">
          <SectionData cols="col-6" label="Label Stock" value={<Override amount={this.state.data.labelStock} />} />
          <SectionData
            cols="col-6"
            label="Print Media Stock"
            value={<Override amount={this.state.data.printMediaStock} />}
          />
        </Row>
        <Row className="section">
          <SectionData cols="col-6 col-sm-6" label="Label Type" value="Address Label" />
          <SectionData
            cols="col-6 col-sm-6"
            label="Label Size (l x h): "
            value={String(this.state.data.labelLength) + ' x ' + String(this.state.data.labelHeight)}
          />
        </Row>
      </DetailAccordionCard>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    machines: getMachines(state),
    fairs: getFairs(state),
  };
};

export default connect(mapStateToProps)(ScaleDetail);
